// src/hooks/usePanelHierarchy.ts
import { useState, useEffect, useCallback } from 'react';

export type HierarchyLevel = 'primary' | 'secondary' | 'tertiary';

interface PanelState {
  hasUrgentItems: boolean;
  itemCount: number;
  lastUpdated: Date;
  isOverBudget?: boolean;
  unreadCount?: number;
  hasP0Items?: boolean;
}

interface UsePanelHierarchyOptions {
  panelId: string;
  state: PanelState;
  currentTime?: Date;
}

export function usePanelHierarchy({ panelId, state, currentTime = new Date() }: UsePanelHierarchyOptions): HierarchyLevel {
  const [hierarchy, setHierarchy] = useState<HierarchyLevel>('secondary');

  const evaluateHierarchy = useCallback(() => {
    const hour = currentTime.getHours();

    // Primary rules (highest priority)
    if (state.isOverBudget) return 'primary';
    if (state.hasP0Items && panelId === 'urgent-panel') return 'primary';
    if (state.itemCount > 0 && panelId === 'focus-panel' && hour >= 20) return 'primary';

    // Secondary rules
    if (state.unreadCount && state.unreadCount > 15 && panelId === 'inbox-panel') return 'secondary';
    if (state.itemCount > 0 && panelId === 'urgent-panel') return 'secondary';
    if (state.itemCount > 0 && panelId === 'focus-panel') return 'secondary';

    // Tertiary rules
    if (panelId === 'timeline-panel') return 'tertiary';
    if (state.itemCount === 0) return 'tertiary';

    return 'secondary';
  }, [panelId, state, currentTime]);

  useEffect(() => {
    const newHierarchy = evaluateHierarchy();
    setHierarchy(newHierarchy);

    // Re-evaluate every 60 seconds
    const interval = setInterval(() => {
      setHierarchy(evaluateHierarchy());
    }, 60000);

    return () => clearInterval(interval);
  }, [evaluateHierarchy]);

  return hierarchy;
}

export default usePanelHierarchy;
